// The home screen app (PWA) opens again on the page it was left on.
//
// iOS starts a home screen app that was closed (swiped away, or dropped after
// a while) from the manifest's start_url (`/`), and the page it was on is
// lost (the person asked for it back, 2026-09-28 on the old web).
//
// Promise:
// - Every page shown in the app (Me, a workspace) is kept on the device.
// - Started as a home screen app from `/`, it goes to the kept page once per
//   launch (a launch is a new browsing session); going back to `/` inside the
//   app later does not jump again.
// - In a browser tab, `/` and links to a page behave as before.
// - Pages outside the app (sign-in, invitations, the waitlist) are not kept.

const KEY = "trameria:last-page";
const RESTORED_KEY = "trameria:last-page-restored";

/** Running as an app added to the home screen. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Keeps the page shown (only the app's own pages: `inApp`). */
export function saveLastPage(href: string, inApp: boolean): void {
  if (!inApp) return;
  try {
    localStorage.setItem(KEY, href);
  } catch {
    // The page works without it.
  }
}

/**
 * Where a home screen app started at `/` goes: the kept page, once per
 * launch; null otherwise. Marks the launch as done.
 */
export function lastPageToRestore(
  pathname: string,
  inApp: (path: string) => boolean,
): string | null {
  if (pathname !== "/" || !isStandalone()) return null;
  try {
    if (sessionStorage.getItem(RESTORED_KEY)) return null;
    sessionStorage.setItem(RESTORED_KEY, "1");
    const href = localStorage.getItem(KEY);
    if (!href?.startsWith("/") || href.startsWith("//")) return null;
    return inApp(new URL(href, window.location.origin).pathname) ? href : null;
  } catch {
    return null;
  }
}
